import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Lenis from 'lenis';
import { Toaster } from 'sonner';
import { LangProvider, useLang } from './i18n';
import Header from './components/Header';
import Hero from './components/Hero';
import Marquee from './components/Marquee';
import Story from './components/Story';
import MissionVision from './components/MissionVision';
import Services from './components/Services';
import Cases from './components/Cases';
import Booking from './components/Booking';
import SitemapMap from './components/SitemapMap';
import Footer from './components/Footer';
import AdminPanel from './components/AdminPanel';
import CrmPage from './pages/CrmPage';
import CasoDetalle from './pages/CasoDetalle';
import AuthCallback from './components/AuthCallback';
import { FloatingWhatsApp } from './components/WhatsAppButton';
import ServicesHome from './pages/ServicesHome';
import Branding from './pages/Branding';
import Digitalizacion from './pages/Digitalizacion';
import Automatizacion from './pages/Automatizacion';
import SeoSem from './pages/SeoSem';
import GrowthPartner from './pages/GrowthPartner';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(err) {
        console.error(err);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-canvas p-8">
                    <p className="font-display text-xl text-ink">Algo salio mal. Recarga la pagina, por favor.</p>
                </div>
            );
        }
        return this.props.children;
    }
}

function Site() {
    const location = useLocation();
    const { t } = useLang();

    useEffect(() => {
        if (location.state?.scrollTo) {
            const id = location.state.scrollTo;
            window.setTimeout(() => {
                const el = document.querySelector(id);
                if (el) {
                    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
                    else el.scrollIntoView({ behavior: 'smooth' });
                }
            }, 450);
        }
    }, [location.state]);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    const onNav = (id) => {
        const el = document.querySelector(id);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
        else el.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="grain bg-canvas text-ink min-h-screen">
            <Header />
            <main>
                <Hero onNav={onNav} />
                <Marquee />
                <Story />
                <MissionVision />
                <Services />
                <Cases />
                <Booking />
                <SitemapMap onNav={onNav} />
            </main>
            <Footer />
            <FloatingWhatsApp message={t('wa_general')} />
        </div>
    );
}

function AppRouter() {
    const location = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/" element={<Site />} />
            <Route path="/servicios" element={<ServicesHome />} />
            <Route path="/servicios/branding" element={<Branding />} />
            <Route path="/servicios/digitalizacion" element={<Digitalizacion />} />
            <Route path="/servicios/automatizacion" element={<Automatizacion />} />
            <Route path="/servicios/seo-sem" element={<SeoSem />} />
            <Route path="/servicios/growth-partner" element={<GrowthPartner />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/crm" element={<CrmPage />} />
            <Route path="/casos/:slug" element={<CasoDetalle />} />
            <Route path="*" element={<Site />} />
        </Routes>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <LangProvider>
                <ErrorBoundary>
                    <AppRouter />
                    <Toaster position="bottom-right" richColors />
                </ErrorBoundary>
            </LangProvider>
        </BrowserRouter>
    );
}
